import { useState } from 'react';

import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';

import { DEFAULT_ERROR_MESSAGE } from '@/constants/app/config';
import { useDispenseAssets } from '@/hooks/useDispenseAssets';
import { usePutCommandQueueTestDispenseMutation } from '@/store/api/services/machines/machine';
import type { CassetteView } from '@/types/generated/athena';

/**
 * Test Dispense Machine Modal
 * Triggers the test_dispense command on a machine via the CommandQueue.
 * Allows the operator to select the number of bills to dispense from each cassette.
 *
 * @param {ModalChildrenProps} { hide, props }
 */
const TestDispenseMachine = ({ hide, props }: ModalChildrenProps) => {
  const cassettes: CassetteView[] = props?.data?.note_dispenser_cassettes ?? [];

  const [counts, setCounts] = useState<Record<number, string>>(
    Object.fromEntries(cassettes.map((c) => [c.cassette_index!, '1'])),
  );

  const [putTestDispense, { isLoading }] = usePutCommandQueueTestDispenseMutation();
  const { billsMap } = useDispenseAssets();

  const handleCountChange = (index: number, value: string) => {
    const numeric = Math.min(1, Math.max(0, Number.parseInt(value, 10) || 0));
    setCounts((prev) => ({ ...prev, [index]: String(numeric) }));
  };

  const handleSubmit = async () => {
    try {
      const args = cassettes
        .map((c) => {
          const raw = counts[c.cassette_index!] ?? '0';
          return String(Number.parseInt(raw, 10) || 0);
        })
        .join(',');

      await putTestDispense({ atmId: props?.data?.id, args }).unwrap();
      toast.success('Test dispense command sent successfully');
      hide();
    } catch (error) {
      toast.error((error as any).message ?? DEFAULT_ERROR_MESSAGE);
    }
  };

  if (!cassettes.length) {
    return (
      <>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" py={2}>
            No cassettes found for this machine.
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={hide}>Close</Button>
        </DialogActions>
      </>
    );
  }

  return (
    <>
      <DialogContent>
        <Stack gap={3} py={2}>
          <Typography variant="body2" color="text.secondary">
            Select the number of bills to dispense from each cassette on machine{' '}
            <strong>{props?.data?.slug}</strong>.
          </Typography>

          {cassettes.map((cassette) => {
            const { cassette_index, bill_note_id, count } = cassette;
            const assetName = billsMap[bill_note_id!]?.at(0);
            const denomination = billsMap[bill_note_id!]?.at(1);
            const label = assetName && denomination ? `${assetName}: $${denomination}` : '—';

            return (
              <Box key={cassette_index}>
                <Typography variant="subtitle2" mb={0.5}>
                  Cassette {cassette_index}
                  {label !== '—' && (
                    <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                      {label}
                    </Typography>
                  )}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={counts[cassette_index!] ?? '1'}
                  onChange={({ target }) => handleCountChange(cassette_index!, target.value)}
                  disabled={isLoading || !bill_note_id}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 0, max: 1 }}
                  InputProps={{
                    endAdornment: count != null && (
                      <InputAdornment position="end">of {count}</InputAdornment>
                    ),
                  }}
                  helperText={!bill_note_id ? 'Cassette is empty' : undefined}
                />
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={hide} disabled={isLoading}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSubmit}
          loading={isLoading}
          disabled={isLoading}
        >
          Send Test Dispense
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default TestDispenseMachine;
